<template>
  <v-card class="Services" elevation="0" tile :disabled="!service.enabled">
    <div class="Services-informationUtilisation">{{ $t('serviceSettings.informationAboutUtilisation') }}</div>
    <div class="Services-dayContainer mb-8">
      <div
        v-for="(day, index) in orderedWeekDays"
        :key="day.key"
        @click="changeDay(index)"
        class="Services-day"
        :class="{ 'Services-day--active': index === indexDay, 'Services-day--disabled': isDisableDay(index) }"
      >
        {{ $t(`dayOfWeek.${day.key}`) }}
      </div>
    </div>
    <div class="Services-hoursContainer">
      <SelectHours
        v-model="intervalSelected"
        @edit="$emit('edit')"
        :increment="service.duration"
        :openings="openingsSelectedDay"
        :service="service"
      />
      <v-row class="mt-12" justify="space-around">
        <v-col class="d-flex align-center" cols="6" md="3" lg="2" order="1" order-md="1">
          <div class="ServiceSettings-label mr-3 pt-3">{{ $t('serviceSettings.durationLabel') }}</div>
          <v-select
            v-model="service.duration"
            @change="$emit('edit')"
            class="d-inline-block"
            dense
            hide-details
            :items="durations"
            filled
          />
        </v-col>
        <v-col class="d-flex align-center" cols="12" md="5" order="3" order-md="2">
          <div class="ServiceSettings-label mr-3 pt-1">{{ $t('serviceSettings.parallelAppointmentLabel') }}</div>
          <v-select
            v-model="service.parallelAppointment"
            @change="$emit('edit')"
            class="d-inline-block"
            dense
            filled
            hide-details
            :items="appointmentNumber"
          />
        </v-col>
        <v-col v-if="displayPrice" cols="6" md="2" order="2" order-md="3">
          <div class="ServiceSettings-label">{{ $t('serviceSettings.priceLabelHard') }}</div>
          <text-field-validation
            :value="String(service.price || '')"
            @change="updatePrice"
            :name="$t('serviceSettings.priceName')"
            :prefix="`${$t('serviceSettings.prefixPrice')} - `"
            rules="decimal|positive"
          />
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import SelectHours from '@/components/settings/SelectHours'
import { orderWeekDays } from '@/services/day'
import { formatHourToFloat, formatHour, rangeTime } from '@/services/time'
import TextFieldValidation from '@/components/lib/inputValidation/textFieldValidation'

export default {
  name: 'ServiceSettings',

  components: { TextFieldValidation, SelectHours },

  model: {
    prop: 'service',
    event: 'change'
  },

  props: {
    service: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      indexDay: 0,
      intervalSelected: []
    }
  },

  computed: {
    ...mapState(['shops']),
    ...mapGetters(['currentShop']),
    appointmentNumber() {
      return [...new Array(6).keys()].slice(1)
    },
    daySelected() {
      return this.orderedWeekDays[this.indexDay].value
    },
    displayPrice() {
      return this.currentShop.country !== 'IT'
    },
    durations() {
      return rangeTime(0.25, 2, 0.25).map(range => ({ text: range.text, value: range.value }))
    },
    openingsSelectedDay() {
      return this.currentShop.openings
        .filter(opening => opening.dayOfWeek === this.daySelected)
        .map(opening => [formatHourToFloat(opening.beginHour), formatHourToFloat(opening.endHour)])
    },
    orderedWeekDays() {
      return orderWeekDays().filter(day => this.currentShop.openings.some(opening => opening.dayOfWeek === day.value))
    }
  },

  watch: {
    intervalSelected() {
      const selectedDay = this.orderedWeekDays[this.indexDay].value
      const otherDays = this.service.openings.filter(opening => opening.dayOfWeek !== selectedDay)
      const currentDay = this.intervalSelected.map(([beginHour, endHour]) => ({
        beginHour: formatHour(beginHour, { withoutSuffix: true }).display,
        dayOfWeek: selectedDay,
        endHour: formatHour(endHour, { withoutSuffix: true }).display
      }))

      this.service.openings = [...otherDays, ...currentDay]
    }
  },

  methods: {
    changeDay(index) {
      if (index === this.indexDay) {
        this.disableDay(index)
        this.$emit('edit')
      }

      this.indexDay = index
      this.setIntervalsSelected()
    },
    disableDay(index) {
      const dayOfWeek = this.orderedWeekDays[index].value
      this.$set(
        this.service,
        'openings',
        this.service.openings.filter(opening => opening.dayOfWeek !== dayOfWeek)
      )
    },
    isDisableDay(index) {
      const dayOfWeek = this.orderedWeekDays[index].value
      return (
        !this.service.openings.some(opening => opening.dayOfWeek === dayOfWeek) ||
        !this.currentShop.openings.some(opening => opening.dayOfWeek === dayOfWeek)
      )
    },
    setIntervalsSelected() {
      if (!this.service.openings) return []

      this.intervalSelected = this.service.openings
        .filter(opening => opening.dayOfWeek === this.orderedWeekDays[this.indexDay].value)
        .map(opening => [
          typeof opening.beginHour === 'number' ? opening.beginHour : formatHourToFloat(opening.beginHour),
          typeof opening.endHour === 'number' ? opening.endHour : formatHourToFloat(opening.endHour)
        ])
        .sort(([a], [b]) => {
          if (a < b) return -1
          if (a > b) return 1
          return 0
        })
    },
    updatePrice(value) {
      let price = Number.parseFloat(value) == value ? Number.parseFloat(value) : value
      if (value === '' || Number(value) === 0) {
        price = undefined
      }
      this.$emit('change', { ...this.service, price })
      this.$emit('edit')
    }
  },

  created() {
    this.setIntervalsSelected()
  }
}
</script>

<style lang="scss">
.Services-informationUtilisation {
  margin-bottom: 12px;
  font-size: 0.9em;
  font-weight: 400;
  color: grey;
}

.Services-dayContainer {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}

.Services-day {
  position: relative;
  padding: 0.4em 1em;
  margin: 0 0.3em 0.6em;
  border-radius: 4px;
  background-color: var(--v-secondary-lighten2);
  font-weight: 300;
  text-transform: capitalize;
  color: var(--v-secondary-base);
  cursor: pointer;
  transition: opacity 30ms;
  &:hover,
  &--active {
    background-color: var(--v-secondary-base);
    color: var(--v-secondary-lighten2);
  }
  &--active::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 100%;
    width: 0;
    height: 0;
    transform: translateX(-50%);
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 8px solid var(--v-secondary-base);
    clear: both;
  }
  &--disabled {
    opacity: 0.45;
  }
}

.ServiceSettings-label {
  color: var(--v-secondary-base);
  font-weight: 300;
  font-size: 0.85em;
}

.App-galileo .Services-day {
  &--active {
    background-color: var(--v-primaryAccent-base);
  }
  &--active::after {
    border-top: 8px solid var(--v-primaryAccent-base);
  }
}
</style>
